import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import dayjs from 'dayjs';
import { isWeekend } from './weekendUtils';
import { DialogHeader, SectionLabel } from './WeekendDialogParts';

const MAX_REASON = 1000;

/** Pick a project and one or more Saturdays / Sundays, then submit for manager approval. */
const WeekendRequestDialog = ({
  open,
  onClose,
  onSubmit,
  projects,
  submitting,
}) => {
  const [projectId, setProjectId] = useState('');
  const [dates, setDates] = useState([]);
  const [pickerValue, setPickerValue] = useState(null);
  const [reason, setReason] = useState('');

  const sortedDates = useMemo(() => [...dates].sort(), [dates]);

  const reset = () => {
    setProjectId('');
    setDates([]);
    setPickerValue(null);
    setReason('');
  };

  const close = () => {
    reset();
    onClose();
  };

  const addDate = (value) => {
    setPickerValue(null);
    if (!value || !value.isValid()) return;
    const iso = value.format('YYYY-MM-DD');
    setDates((prev) => (prev.includes(iso) ? prev : [...prev, iso]));
  };

  const canSubmit =
    projectId !== '' && dates.length > 0 && reason.trim() !== '' && !submitting;

  return (
    <Dialog
      open={open}
      onClose={close}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { borderRadius: 3 } }}
    >
      <DialogHeader
        icon={<EventAvailableOutlinedIcon />}
        title="Request weekend work"
        subtitle="Your project manager approves or rejects the dates."
      />
      <DialogContent sx={{ pt: '20px !important' }}>
        <Box sx={{ display: 'grid', gap: 2.5 }}>
          <Box>
            <SectionLabel>Project</SectionLabel>
            <TextField
              select
              fullWidth
              size="small"
              label="Select project"
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
            >
              {projects.map((project) => (
                <MenuItem key={project.projectId} value={project.projectId}>
                  {project.projectName}
                </MenuItem>
              ))}
            </TextField>
          </Box>

          <Box>
            <SectionLabel>Dates</SectionLabel>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="Add a Saturday or Sunday"
                value={pickerValue}
                shouldDisableDate={(day) => !isWeekend(day)}
                onChange={addDate}
                renderInput={(params) => (
                  <TextField {...params} size="small" fullWidth />
                )}
              />
            </LocalizationProvider>
            <Box
              sx={{
                mt: 1.5,
                p: 1.5,
                minHeight: 52,
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1,
                borderRadius: 2,
                border: '1px dashed',
                borderColor: 'divider',
                bgcolor: 'action.hover',
              }}
            >
              {sortedDates.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Selected dates appear here. Only Saturdays and Sundays can be
                  picked.
                </Typography>
              ) : (
                sortedDates.map((iso) => (
                  <Chip
                    key={iso}
                    color="primary"
                    variant="outlined"
                    label={dayjs(iso).format('ddd, DD MMM YYYY')}
                    onDelete={() =>
                      setDates((prev) => prev.filter((d) => d !== iso))
                    }
                  />
                ))
              )}
            </Box>
          </Box>

          <Box>
            <SectionLabel>Reason</SectionLabel>
            <TextField
              fullWidth
              required
              size="small"
              label="Why is weekend work needed?"
              value={reason}
              multiline
              minRows={4}
              maxRows={4}
              inputProps={{ maxLength: MAX_REASON }}
              helperText={`${reason.length}/${MAX_REASON}`}
              onChange={(e) => setReason(e.target.value)}
            />
          </Box>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={close}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!canSubmit}
          onClick={async () => {
            const ok = await onSubmit({
              projectId,
              dates: sortedDates,
              reason: reason.trim(),
            });
            if (ok) reset();
          }}
        >
          Submit request
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WeekendRequestDialog;
